import { MsalProvider } from '@azure/msal-react'
import { ColorModeScript } from '@chakra-ui/react'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import * as React from 'react'
import * as ReactDOM from 'react-dom/client'
import { AuthProvider } from './context/AuthContext'
import './core/i18n/config'
import { initDatadog } from './datadogConfig'
import { msalInstance } from './msalInstance'
import reportWebVitals from './reportWebVitals'
import AppRoutes from './routes'
import * as serviceWorker from './serviceWorker'

const isDevelopment = process.env.NODE_ENV === 'development'

const container = document.getElementById('root')
if (!container) throw new Error('Failed to find the root element')
const root = ReactDOM.createRoot(container)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
})

if (!isDevelopment) {
  initDatadog()
}

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'react-query-cache',
  throttleTime: 1000, // throttle saves to once per second
})

root.render(
  <React.StrictMode>
    <ColorModeScript />
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister: localStoragePersister,
            // Bump `buster` whenever persisted auth semantics change so stale
            // caches (e.g. a non-superuser `user` from a pre-auth-bypass
            // session) are discarded instead of rehydrated indefinitely.
            buster: 'auth-v2',
            dehydrateOptions: {
              shouldDehydrateQuery: query => {
                return query.queryKey.includes('user')
              },
            },
          }}
        >
          <AppRoutes />
          {isDevelopment && <ReactQueryDevtools initialIsOpen={false} position='right' />}
        </PersistQueryClientProvider>
      </AuthProvider>
    </MsalProvider>
  </React.StrictMode>,
)

serviceWorker.unregister()
reportWebVitals()
